<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-backup-restore"
                title="Return Stock"
                subtitle="Goods that went back through returns, and damaged pieces held aside (not sellable)"
                style="width: 100%"
              />
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search product name, color or size... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.type"
                    :items="typeOptions"
                    item-text="text"
                    item-value="value"
                    label="Return Type"
                    prepend-inner-icon="mdi-filter-variant"
                    clearable
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Totals for the current filters -->
      <v-row>
        <v-col cols="12" sm="4">
          <v-card elevation="0" style="border: 1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Purchase Returns</div>
                <div class="text-h4 font-weight-bold orange--text">{{ summary.purchase_quantity }}</div>
                <div class="text-caption mt-1 grey--text">Units sent back to vendors</div>
              </div>
              <v-avatar size="48" color="orange lighten-4">
                <v-icon color="orange" size="26">mdi-truck-fast-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card elevation="0" style="border: 1px solid #e0e0e0" class="fill-height">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Sales Returns</div>
                <div class="text-h4 font-weight-bold teal--text">{{ summary.sale_quantity }}</div>
                <div class="text-caption mt-1 grey--text">Units given back by customers</div>
              </div>
              <v-avatar size="48" color="teal lighten-4">
                <v-icon color="teal" size="26">mdi-keyboard-return</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card
            elevation="0"
            style="border: 1px solid #e0e0e0; cursor: pointer"
            class="fill-height"
            @click="(filter.type = 'damaged'), (page = 1), getData()"
          >
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption grey--text">Damaged (held)</div>
                <div class="text-h4 font-weight-bold red--text">{{ summary.damaged_quantity || 0 }}</div>
                <div class="text-caption mt-1 grey--text">Not sellable - move to stock or write off</div>
              </div>
              <v-avatar size="48" color="red lighten-4">
                <v-icon color="red" size="26">mdi-alert-octagon-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="500" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Product Name</th>
                      <th class="text-left">Color</th>
                      <th class="text-left">Size</th>
                      <th class="text-left">Quantity</th>
                      <th class="text-left">Returns</th>
                      <th class="text-right">Value</th>
                      <th class="text-left">Last Returned</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="stocks.length != 0">
                    <tr
                      v-for="(stock, i) in stocks"
                      :key="`rs_${stock.type}_${stock.variant_id}`"
                      class="clickable-row"
                      @click="viewDetails(stock)"
                    >
                      <td style="min-width: 85px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td>
                        <v-chip small :color="typeColors[stock.type]" text-color="white">
                          <v-avatar left>
                            <v-icon size="15">{{ typeIcons[stock.type] }}</v-icon>
                          </v-avatar>
                          {{ stock.type_label }}
                        </v-chip>
                      </td>
                      <td>{{ stock.product_name }}</td>
                      <td>{{ stock.color }}</td>
                      <td>{{ stock.size }}</td>
                      <td class="font-weight-bold">{{ stock.quantity }}</td>
                      <td>{{ stock.returns_count }}</td>
                      <td class="text-right mono-figure">₹{{ stock.amount.toFixed(2) }}</td>
                      <td>{{ stock.last_returned_at | niceDate }}</td>
                      <td class="text-right action_th">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="primary"
                              @click.stop="viewDetails(stock)"
                            >
                              <v-icon dark>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ stock.type === "damaged" ? "History" : "See the returns behind this" }}</span>
                        </v-tooltip>
                        <template v-if="stock.type === 'damaged'">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto ml-1"
                                color="success"
                                @click.stop="openAction(stock, 'move')"
                              >
                                <v-icon dark>mdi-package-variant-closed-check</v-icon>
                              </v-btn>
                            </template>
                            <span>Move to stock (checked / repaired)</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                class="my-auto ml-1"
                                color="error"
                                @click.stop="openAction(stock, 'writeoff')"
                              >
                                <v-icon dark>mdi-delete-forever-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Write off</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="10">
                        <AppEmptyState
                          icon="mdi-backup-restore"
                          title="No returned stock"
                          message="Items sent back to vendors, items customers return and damaged pieces held aside will show up here."
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- The individual returns behind one row -->
    <AppDialog
      :show.sync="showDetails"
      max-width="850"
      :icon="selected ? typeIcons[selected.type] : ''"
      :title="selected ? `${selected.product_name} - ${[selected.color, selected.size].filter(Boolean).join(' - ')}` : ''"
      :subtitle="selected ? `${selected.type_label} · ${selected.quantity} unit(s)` : ''"
    >
      <section v-if="isLoadingDetails" class="py-6">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </section>
      <v-simple-table v-else class="striped-table" dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">{{ selected && selected.type === "damaged" ? "What happened" : "Return #" }}</th>
              <th class="text-left">Date</th>
              <th class="text-left">{{ partyHeader }}</th>
              <th class="text-left">{{ selected && selected.type === "purchase" ? "PO #" : "Order #" }}</th>
              <th class="text-left">Reason</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in details" :key="`d_${line.return_id}_${line.quantity}_${line.amount}`">
              <td>
                <span v-if="selected.type === 'damaged'">{{ line.return_number }}</span>
                <router-link v-else :to="selected.type === 'purchase' ? '/purchase-returns' : '/sale-returns'">
                  {{ line.return_number }}
                </router-link>
              </td>
              <td>{{ line.returned_at | niceDate }}</td>
              <td>{{ line.party_name || "-" }}</td>
              <td>{{ line.reference || "-" }}</td>
              <td>{{ line.reason || "-" }}</td>
              <td class="text-right">{{ line.quantity }}</td>
              <td class="text-right mono-figure">{{ line.amount === null || line.amount === undefined ? "-" : `₹${line.amount}` }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </AppDialog>

    <!-- Damaged pieces: back to sellable stock, or written off -->
    <AppDialog
      :show.sync="showAction"
      max-width="480"
      :icon="action === 'move' ? 'mdi-package-variant-closed-check' : 'mdi-delete-forever-outline'"
      :title="action === 'move' ? 'Move to stock' : 'Write off'"
      :subtitle="actionSubtitle"
    >
      <div class="text-body-2 mb-3">
        {{
          action === "move"
            ? "Checked or repaired pieces go back to sellable company stock."
            : "Pieces that can't be sold leave stock for good."
        }}
      </div>
      <v-text-field
        v-model.number="actionForm.quantity"
        type="number"
        min="1"
        :max="actionStock ? actionStock.quantity : 1"
        outlined
        dense
        label="Pieces"
        :error-messages="actionError"
      ></v-text-field>
      <v-text-field v-model="actionForm.note" outlined dense label="Note (optional)" hide-details></v-text-field>
      <template v-slot:actions>
        <v-btn :color="action === 'move' ? 'success' : 'error'" depressed :loading="isActing" @click="submitAction">
          {{ action === "move" ? "Move to stock" : "Write off" }}
        </v-btn>
      </template>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";

export default {
  name: "returnStocksPage",
  components: { AppDialog, AppEmptyState, AppPageHeader },
  data() {
    return {
      filter: { search_keyword: null, type: null },
      page: 1,
      paginationLength: 0,
      count: 0,
      stocks: [],
      summary: { purchase_quantity: 0, sale_quantity: 0, damaged_quantity: 0 },
      isLoading: false,
      typeOptions: [
        { text: "Purchase Return", value: "purchase" },
        { text: "Sales Return", value: "sale" },
        { text: "Damaged (held)", value: "damaged" },
      ],
      typeColors: { purchase: "orange", sale: "teal", damaged: "red" },
      typeIcons: { purchase: "mdi-truck-fast-outline", sale: "mdi-keyboard-return", damaged: "mdi-alert-octagon-outline" },
      // Damaged pieces: move to stock / write off
      showAction: false,
      action: null,
      actionStock: null,
      actionForm: { quantity: 1, note: "" },
      actionError: "",
      isActing: false,
      showDetails: false,
      selected: null,
      details: [],
      isLoadingDetails: false,
    };
  },
  computed: {
    partyHeader() {
      if (!this.selected) return "";
      return { purchase: "Vendor", sale: "Customer", damaged: "By" }[this.selected.type] || "";
    },
    actionSubtitle() {
      const s = this.actionStock;
      if (!s) return "";
      return `${s.product_name} - ${[s.color, s.size].filter(Boolean).join(" - ")} · ${s.quantity} held`;
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        search_keyword: this.filter.search_keyword || "",
        type: this.filter.type || "",
      });
      await axios
        .get(`return_stocks?${params.toString()}`)
        .then((response) => {
          this.stocks = response.data.data;
          this.count = response.data.count;
          this.summary = response.data.summary || this.summary;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async viewDetails(stock) {
      this.selected = stock;
      this.details = [];
      this.showDetails = true;
      this.isLoadingDetails = true;
      await axios
        .get(`return_stocks/${stock.type}/${stock.variant_id}`)
        .then((response) => {
          this.details = response.data.data || [];
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoadingDetails = false;
    },
    openAction(stock, action) {
      this.actionStock = stock;
      this.action = action;
      this.actionForm = { quantity: stock.quantity, note: "" };
      this.actionError = "";
      this.showAction = true;
    },
    async submitAction() {
      const qty = Number(this.actionForm.quantity);
      if (!qty || qty < 1 || qty > this.actionStock.quantity) {
        this.actionError = `Enter 1 to ${this.actionStock.quantity}`;
        return;
      }
      this.isActing = true;
      const path = this.action === "move" ? "move-to-stock" : "write-off";
      await axios
        .post(`return_stocks/damaged/${this.actionStock.variant_id}/${path}`, {
          quantity: qty,
          note: this.actionForm.note || null,
        })
        .then((response) => {
          this.showAction = false;
          if (this.$toast) this.$toast.success(response.data.message);
          this.getData();
        })
        .catch((error) => {
          this.actionError = (error.response && error.response.data.message) || "Could not save.";
        });
      this.isActing = false;
    },
  },
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover > td {
  background-color: #FBF7EF !important;
}
</style>
